import {
    Box,
    Container,
    Grid,
    Stack,
    Typography,
    Chip,
    Alert, Button, Divider
} from "@mui/joy";
import "firebase/compat/auth"
import React, {useContext, useEffect, useState} from "react";
import {AuthContext} from "../utils/AuthProvider";
import {FeedbackContext} from "../utils/FeedbackContext";
import {ConfirmDialog} from "../components/ConfirmDialog";
import {GlassCard} from "../components/GlassCard";
import {Layout} from "../components/Layout";
import {Invoices, Subscription} from "../utils/Model";
import {
    Cancel, CheckCircle, Edit, Help, Refresh, Sell,
    VideocamRounded, LockOpenRounded, AllInclusiveRounded, AutoAwesomeRounded
} from "@mui/icons-material";
import moment from "moment";
import {Link} from "react-router-dom";
import {APIurl, intervalToString, stringPrice} from "../utils/Utils";
import firebase from "firebase/compat/app";
import {useTranslation} from "react-i18next";


export function Account() {

    const { t } = useTranslation();
    const {userData, subscriptions} = useContext(AuthContext)
    const [invoices, setInvoices] = useState<Invoices | null>(null)
    const hasExpiredPaymentMethod = subscriptions.some(
        (subscription: Subscription) => subscription.paymentMethodExpiredAtRenewal === true
    );

    useEffect(() => {
        if(subscriptions.length > 0) {
            fetchInvoices().then(r => setInvoices(r.invoices))
        }
    }, [subscriptions])

    async function fetchInvoices(): Promise<{ invoices: Invoices }> {
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error(`User not authenticated`);
            }

            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/getInvoices/${userData.uid}/${subscriptions[0].id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            return await response.json();

        } catch (error) {
            throw error;
        }
    }

    return (
        <Layout>
            <Container sx={{py: {xs: 3, sm: 5}, px: {xs: 2, sm: 3}}}>
                <Stack spacing={4} sx={{maxWidth: 1000, mx: "auto"}}>

                    <Stack spacing={0.5}>
                        <Typography level="h2" sx={{fontSize: {xs: "1.6rem", sm: "2.1rem"}}}>{t("ciao")} {userData.name}!</Typography>
                        <Typography level="body-md" sx={{color: "neutral.400"}}>{t("manageabbonamento")}</Typography>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography level="title-lg">{t("yourprofile")}</Typography>
                        <CardProfilo />
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography level="title-lg">{t("yoursubscription")}</Typography>

                        {
                            subscriptions.length > 0 && hasExpiredPaymentMethod &&
                            <Alert variant="soft" color="warning" sx={{borderRadius: 16}}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    sx={{ width: "100%" }}
                                >
                                    <Stack spacing={0.5}>
                                        <Typography level="title-md">{t("paymentmethodexpiredtitle")}</Typography>
                                        <Typography level="body-sm">{t("paymentmethodexpiredlabel")}</Typography>
                                    </Stack>

                                    <Link to="/update-payment-method" style={{width: "100%"}}>
                                        <Button variant="solid" color="warning" startDecorator={<Edit />} sx={{width: {xs: "100%", md: "auto"}}}>
                                            {t("updatepaymentmethod")}
                                        </Button>
                                    </Link>
                                </Stack>
                            </Alert>
                        }

                        {
                            subscriptions.length !== 0 ?
                                subscriptions.map((subscription: Subscription) => (
                                    <CardSubscription key={subscription.id} subscription={subscription} />
                                ))
                                :
                                <SubscribePromo />
                        }
                    </Stack>

                </Stack>
            </Container>
        </Layout>
    )

    // Card promozionale al posto del vecchio semplice Alert — spinge
    // all'abbonamento con gli stessi 4 vantaggi mostrati nello sheet
    // "Ottieni Premium" lato Flutter, per coerenza tra le due piattaforme.
    function SubscribePromo() {
        const benefits = [
            {icon: <VideocamRounded />, title: t("promobenefit1title"), subtitle: t("promobenefit1subtitle")},
            {icon: <LockOpenRounded />, title: t("promobenefit2title"), subtitle: t("promobenefit2subtitle")},
            {icon: <AllInclusiveRounded />, title: t("promobenefit3title"), subtitle: t("promobenefit3subtitle")},
            {icon: <AutoAwesomeRounded />, title: t("promobenefit4title"), subtitle: t("promobenefit4subtitle")},
        ];

        return (
            <Box
                sx={{
                    p: {xs: 3, sm: 4},
                    borderRadius: 20,
                    background: "linear-gradient(135deg, rgba(0,122,255,0.16), rgba(175,82,222,0.10))",
                    border: "1px solid rgba(0,122,255,0.30)",
                }}
            >
                <Stack spacing={3}>
                    <Stack spacing={0.5}>
                        <Typography level="title-lg">{t("accountpromotitle")}</Typography>
                        <Typography level="body-sm" sx={{color: "neutral.300"}}>{t("accountpromosubtitle")}</Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        {benefits.map((b, i) => (
                            <Grid key={i} xs={12} sm={6}>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <Box sx={{
                                        width: 34, height: 34, borderRadius: "50%",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        backgroundColor: "rgba(0,122,255,0.18)", color: "#3395ff", flexShrink: 0,
                                    }}>
                                        {b.icon}
                                    </Box>
                                    <Stack spacing={0}>
                                        <Typography level="title-sm">{b.title}</Typography>
                                        <Typography level="body-xs" sx={{color: "neutral.400"}}>{b.subtitle}</Typography>
                                    </Stack>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>

                    <Link to="/billing-details">
                        <Button variant="solid" color="primary" size="lg" startDecorator={<Sell />} sx={{width: {xs: "100%", sm: "auto"}}}>
                            {t("subscribe")}
                        </Button>
                    </Link>
                </Stack>
            </Box>
        )
    }

    function CardProfilo() {
        return (
            <GlassCard>
                <Grid container spacing={2}>
                    <Grid xs={6} sm={6} md={3}>
                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("name")}</Typography>
                        <Typography level="body-lg" fontWeight="bold">{userData.name}</Typography>
                    </Grid>
                    <Grid xs={6} sm={6} md={3}>
                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("surname")}</Typography>
                        <Typography level="body-lg" fontWeight="bold">{userData.surname}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("email")}</Typography>
                        <Typography level="body-lg" fontWeight="bold" sx={{wordBreak: "break-word"}} fontSize={{xs:"0.85rem", md:"inherit"}}>{userData.email}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("uid")}</Typography>
                        <Typography level="body-lg" fontFamily="monospace" sx={{wordBreak: "break-word"}} fontSize={{xs:"0.75rem", md:"inherit"}}>{userData.uid}</Typography>
                    </Grid>
                </Grid>
            </GlassCard>
        )
    }

    function CardSubscription(props: {subscription: Subscription}) {
        const subscription = props.subscription;
        const {refreshUserData} = useContext(AuthContext);
        const {setError, setMessage} = useContext(FeedbackContext);
        const [confirmOpen, setConfirmOpen] = useState<"cancel" | "restore" | null>(null);
        const [actionLoading, setActionLoading] = useState(false);

        async function onConfirmCancel() {
            setActionLoading(true);
            try {
                await deleteSubscription(subscription.id);
                await refreshUserData();
                setConfirmOpen(null);
                setMessage(t("subscriptioncancelledsuccess"));
            } catch (e: unknown) {
                setError(e);
            } finally {
                setActionLoading(false);
            }
        }

        async function onConfirmRestore() {
            setActionLoading(true);
            try {
                await restoreSubscription(subscription.id);
                await refreshUserData();
                setConfirmOpen(null);
                setMessage(t("subscriptionrestoredsuccess"));
            } catch (e: unknown) {
                setError(e);
            } finally {
                setActionLoading(false);
            }
        }

        switch (subscription.status) {
            case "active":
                return (
                    <GlassCard sx={{position: "relative"}}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("status")}</Typography>
                                <Chip size="md" variant="solid" color="success" startDecorator={<CheckCircle />}>{t("active")}</Chip>
                            </Grid>
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("startsubscription")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{moment(subscription.start_date, "X").format("DD/MM/YYYY")}</Typography>
                            </Grid>
                            {
                                subscription.cancel_at === null ?
                                    <Grid xs={6} md={2}>
                                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("nextrenew")}</Typography>
                                        <Typography level="body-lg" fontWeight="bold">{moment(subscription.current_period_end, "X").format("DD/MM/YYYY")}</Typography>
                                    </Grid>
                                    :
                                    null
                            }
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("frequency")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{intervalToString(subscription.items.data[0].plan.interval)}</Typography>
                            </Grid>
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("annualprice")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{stringPrice(subscription.items.data[0].price.currency, subscription.items.data[0].price.unit_amount)}</Typography>
                            </Grid>
                            <Grid xs={12}>
                                <Divider />
                                <Typography level="title-sm" mt={3} fontWeight="bold">{t("invoices")}</Typography>
                            </Grid>
                            <Grid xs={12}>
                                <Stack direction="column" spacing={1.5}>
                                {
                                    invoices?.data.sort((a, b) => b.created - a.created).map(inv =>
                                        <Box
                                            key={inv.id}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 12,
                                                backgroundColor: "rgba(255,255,255,0.03)",
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid xs={6} md={2}>
                                                    <Stack direction="column" spacing={0}>
                                                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("invoicenumber")}</Typography>
                                                        <Typography level="body-lg" fontWeight="bold">{inv.number}</Typography>
                                                    </Stack>
                                                </Grid>
                                                <Grid xs={6} md={2}>
                                                    <Stack direction="column" spacing={0}>
                                                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("date")}</Typography>
                                                        <Typography level="body-lg" fontWeight="bold">{moment(inv.created, "X").format("DD/MM/YYYY")}</Typography>
                                                    </Stack>
                                                </Grid>
                                                <Grid xs={6} md={2}>
                                                    <Stack direction="column" spacing={0}>
                                                        <Typography level="title-sm" sx={{color: "neutral.400"}}>{t("price")}</Typography>
                                                        <Typography level="body-lg" fontWeight="bold">{stringPrice(inv.currency, inv.amount_paid)}</Typography>
                                                    </Stack>
                                                </Grid>
                                                <Grid xs={6} md={2}>
                                                    <Stack direction="column" spacing={0}>
                                                        <a href={inv.hosted_invoice_url!} rel="noreferrer" target="_blank">
                                                            <Button color="primary" variant="soft" size="sm">{t("downloadinvoice")}</Button>
                                                        </a>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )
                                }
                                </Stack>
                            </Grid>
                            {
                                subscription.cancel_at !== null ?
                                    <Grid xs={12}>
                                        <Alert variant="soft" color="warning" sx={{borderRadius: 14}}>
                                            <Stack spacing={2} direction={{xs: "column", md: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", md: "center"}} sx={{width: "100%"}}>
                                                <Stack>
                                                    <Typography level="body-sm">{t("indata")} {moment(subscription.canceled_at, "X").format("DD/MM/YYYY")} {t("hairichiestoannullamento")}</Typography>
                                                    <Typography level="body-sm">{t("subscriptionwillbeinterruptedon")} <b>{moment(subscription.cancel_at, "X").format("DD/MM/YYYY")}</b>.</Typography>
                                                </Stack>
                                                <Button variant="solid" color="primary" startDecorator={<Refresh />} onClick={() => setConfirmOpen("restore")} sx={{width: {xs: "100%", md: "auto"}}}>
                                                    {t("restoresubscription")}
                                                </Button>
                                            </Stack>
                                        </Alert>
                                    </Grid>
                                    :
                                    <Grid xs={12}>
                                        <Button variant="outlined" color="danger" startDecorator={<Cancel />} onClick={() => setConfirmOpen("cancel")}>
                                            {t("deletesubscription")}
                                        </Button>
                                    </Grid>
                            }
                        </Grid>

                        <ConfirmDialog
                            open={confirmOpen === "cancel"}
                            title={t("canceldialogtitle")}
                            description={t("canceldialogdescription", {date: moment(subscription.current_period_end, "X").format("DD/MM/YYYY")})}
                            confirmLabel={t("canceldialogconfirm")}
                            cancelLabel={t("closelabel")}
                            confirmColor="danger"
                            loading={actionLoading}
                            onConfirm={onConfirmCancel}
                            onClose={() => setConfirmOpen(null)}
                        />
                        <ConfirmDialog
                            open={confirmOpen === "restore"}
                            title={t("restoredialogtitle")}
                            description={t("restoredialogdescription")}
                            confirmLabel={t("restoredialogconfirm")}
                            cancelLabel={t("closelabel")}
                            confirmColor="primary"
                            loading={actionLoading}
                            onConfirm={onConfirmRestore}
                            onClose={() => setConfirmOpen(null)}
                        />
                    </GlassCard>
                )
            case "canceled":
                return <Chip size="md" variant="solid" color="danger" startDecorator={<Cancel />}>{t("cancelled")}</Chip>
            case "ended":
                return <Chip size="md" variant="solid" color="danger" startDecorator={<Cancel />}>{t("ended")}</Chip>
            default:
                return <Chip size="md" variant="solid" color="neutral" startDecorator={<Help />}>{t("notavailable")}</Chip>
        }
    }

    async function deleteSubscription(id: string) {
        const user = firebase.auth().currentUser;
        if (!user) {
            throw new Error(t("usernotauthenticated"));
        }
        const token = await user.getIdToken();

        const response = await fetch(`${APIurl}/deleteSubscription/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        })
        if (!response.ok) {
            throw new Error(t("genericerror"));
        }

        return await response.json();
    }

    async function restoreSubscription(id: string) {
        const user = firebase.auth().currentUser;
        if (!user) {
            throw new Error(t("usernotauthenticated"));
        }
        const token = await user.getIdToken();

        const response = await fetch(`${APIurl}/restoreSubscription/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        })
        if (!response.ok) {
            throw new Error(t("genericerror"));
        }

        return await response.json();
    }
}
